import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Scan } from "lucide-react";
import {
  createContext,
  type PointerEvent,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";

import { useIsMobile } from "@/hooks/useIsMobile";
import {
  type CanvasCaptureArea,
  CanvasCore,
  type CanvasSnapshot,
} from "@/lib/canvas";
import { Template as TemplateRenderer } from "@/lib/template";

import { useAppContext } from "./AppContext";
import { useAudio } from "./AudioContext";
import { useTemplateContext } from "./TemplateContext";

const TEMPLATE_CAPTURE_PADDING = 2;
const REGION_CLICK_THRESHOLD = 4;
const SNAPSHOT_FEEDBACK_DELAY_MS = 250;
const POLAROID_PADDING = 8;
const POLAROID_BOTTOM_PADDING = 34;
const POLAROID_TARGET_SCALE = 0.32;

interface RegionPoint {
  clientX: number;
  clientY: number;
  canvasX: number;
  canvasY: number;
}

interface RegionSelection {
  start: RegionPoint;
  current: RegionPoint;
}

interface SnapshotPreview {
  snapshot: CanvasSnapshot;
  width: number;
  height: number;
  photoHeight: number;
  targetX: number;
  targetY: number;
}

interface CaptureContextValue {
  captureCanvas: () => void;
  captureView: () => void;
  captureRegion: () => void;
  captureTemplateArea: () => void;
  hasTemplateCapture: boolean;
}

const context = createContext<CaptureContextValue | null>(null);

export const useCaptureContext = () => {
  const value = useContext(context);

  if (!value) {
    throw new Error("useCaptureContext must be used inside CaptureContext");
  }

  return value;
};

const createSnapshotPreview = (snapshot: CanvasSnapshot): SnapshotPreview => {
  const sourceWidth = Math.max(snapshot.width, 1);
  const sourceHeight = Math.max(snapshot.height, 1);
  const maxWidth = Math.min(296, Math.max(196, window.innerWidth * 0.48));
  const maxPhotoSize = maxWidth - POLAROID_PADDING * 2;
  const isPortrait = sourceHeight > sourceWidth;
  const photoWidth = isPortrait
    ? Math.max(1, Math.round(maxPhotoSize * (sourceWidth / sourceHeight)))
    : maxPhotoSize;
  const photoHeight = isPortrait
    ? maxPhotoSize
    : Math.max(1, Math.round(photoWidth * (sourceHeight / sourceWidth)));
  const width = photoWidth + POLAROID_PADDING * 2;
  const height = photoHeight + POLAROID_PADDING + POLAROID_BOTTOM_PADDING;
  const targetRight = 48;
  const targetTop = -96;

  return {
    snapshot,
    width,
    height,
    photoHeight,
    targetX:
      window.innerWidth / 2 - targetRight - (width * POLAROID_TARGET_SCALE) / 2,
    targetY:
      targetTop + (height * POLAROID_TARGET_SCALE) / 2 - window.innerHeight / 2,
  };
};

const CaptureSnapshotFeedback = () => {
  const prefersReducedMotion = useReducedMotion();
  const { settings } = useAppContext();
  const { play } = useAudio();
  const cameraFlashEnabled = settings.get["capture.cameraFlash"];
  const pendingSnapshot = useRef<CanvasSnapshot | null>(null);
  const snapshotDelay = useRef<number | undefined>(undefined);
  const [snapshotFlashId, setSnapshotFlashId] = useState<number>();
  const [snapshotPreview, setSnapshotPreview] =
    useState<SnapshotPreview | null>(null);

  useEffect(() => {
    const canvas = CanvasCore.get();
    const clearSnapshotDelay = () => {
      if (typeof snapshotDelay.current === "undefined") return;

      window.clearTimeout(snapshotDelay.current);
      snapshotDelay.current = undefined;
    };
    const handleSnapshot = (snapshot: CanvasSnapshot) => {
      play("CAMERA_SHUTTER");
      setSnapshotFlashId(cameraFlashEnabled ? snapshot.id : undefined);
      pendingSnapshot.current?.save();
      pendingSnapshot.current = null;
      clearSnapshotDelay();
      setSnapshotPreview((current) => {
        current?.snapshot.save();
        return null;
      });

      if (prefersReducedMotion) {
        pendingSnapshot.current = snapshot;
        snapshotDelay.current = window.setTimeout(() => {
          pendingSnapshot.current = null;
          snapshotDelay.current = undefined;
          snapshot.save();
        }, SNAPSHOT_FEEDBACK_DELAY_MS);
        return;
      }

      pendingSnapshot.current = snapshot;
      snapshotDelay.current = window.setTimeout(() => {
        pendingSnapshot.current = null;
        snapshotDelay.current = undefined;
        setSnapshotPreview(createSnapshotPreview(snapshot));
      }, SNAPSHOT_FEEDBACK_DELAY_MS);
    };

    canvas.on("snapshot", handleSnapshot);

    return () => {
      canvas.off("snapshot", handleSnapshot);
      clearSnapshotDelay();
      pendingSnapshot.current?.save();
      pendingSnapshot.current = null;
    };
  }, [cameraFlashEnabled, play, prefersReducedMotion]);

  const handlePolaroidComplete = (snapshot: CanvasSnapshot) => {
    snapshot.save();
    setSnapshotPreview((current) =>
      current?.snapshot.id === snapshot.id ? null : current,
    );
  };

  return (
    <>
      <AnimatePresence>
        {cameraFlashEnabled && snapshotFlashId && (
          <motion.div
            key={snapshotFlashId + "-flash"}
            className="pointer-events-none fixed inset-0 z-[88] bg-white"
            aria-hidden="true"
            initial={{ opacity: prefersReducedMotion ? 0.35 : 0.68 }}
            animate={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: prefersReducedMotion ? 0.22 : 0.42 }}
            onAnimationComplete={() => {
              setSnapshotFlashId((current) =>
                current === snapshotFlashId ? undefined : current,
              );
            }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {snapshotPreview && (
          <motion.div
            key={snapshotPreview.snapshot.id}
            className="pointer-events-none fixed left-1/2 top-1/2 z-[90]"
            aria-hidden="true"
            style={{
              width: snapshotPreview.width,
              minWidth: snapshotPreview.width,
              maxWidth: snapshotPreview.width,
            }}
            initial={{
              opacity: 1,
              x: -snapshotPreview.width / 2,
              y: -snapshotPreview.height / 2,
              scale: 0,
              rotate: 0,
            }}
            animate={{
              opacity: [1, 1, 1, 1, 0],
              x: [
                -snapshotPreview.width / 2,
                -snapshotPreview.width / 2,
                -snapshotPreview.width / 2,
                snapshotPreview.targetX,
                snapshotPreview.targetX,
              ],
              y: [
                -snapshotPreview.height / 2,
                -snapshotPreview.height / 2,
                -snapshotPreview.height / 2,
                snapshotPreview.targetY,
                snapshotPreview.targetY,
              ],
              scale: [0, 1.08, 1, 0.4, 0.32],
              rotate: [0, 0, 0, 18, 18],
            }}
            transition={{
              duration: 1.45,
              times: [0, 0.3, 0.56, 0.86, 1],
              ease: "easeInOut",
            }}
            onAnimationComplete={() =>
              handlePolaroidComplete(snapshotPreview.snapshot)
            }
          >
            <div
              className="relative rounded-sm bg-white p-2 shadow-2xl ring-1 ring-black/15"
              style={{
                width: snapshotPreview.width,
                minWidth: snapshotPreview.width,
                maxWidth: snapshotPreview.width,
                boxSizing: "border-box",
                paddingBottom: POLAROID_BOTTOM_PADDING,
              }}
            >
              <div
                className="overflow-hidden bg-zinc-100 ring-1 ring-black/10"
                style={{
                  width: "100%",
                  height: snapshotPreview.photoHeight,
                }}
              >
                <img
                  src={snapshotPreview.snapshot.src}
                  alt=""
                  className="block h-full w-full object-fill [image-rendering:pixelated]"
                  draggable={false}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const CaptureContext = ({ children }: PropsWithChildren) => {
  const { config } = useAppContext();
  const isMobile = useIsMobile();
  const template = useTemplateContext();
  const canvasSize = config?.canvas.size;
  const [captureRegionMode, setCaptureRegionMode] = useState(false);
  const regionAnchorRef = useRef<RegionPoint | null>(null);
  const regionSelectionRef = useRef<RegionSelection | null>(null);
  const [regionSelection, setRegionSelection] =
    useState<RegionSelection | null>(null);

  const updateRegionAnchor = useCallback((anchor: RegionPoint | null) => {
    regionAnchorRef.current = anchor;
  }, []);

  const updateRegionSelection = useCallback(
    (selection: RegionSelection | null) => {
      regionSelectionRef.current = selection;
      setRegionSelection(selection);
    },
    [],
  );

  const resetCaptureRegion = useCallback(() => {
    document.body.classList.remove("is-capturing-region");
    setCaptureRegionMode(false);
    updateRegionAnchor(null);
    updateRegionSelection(null);
  }, [updateRegionAnchor, updateRegionSelection]);

  useEffect(() => {
    if (!captureRegionMode) return;

    document.body.classList.add("is-capturing-region");

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      resetCaptureRegion();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("is-capturing-region");
    };
  }, [captureRegionMode, resetCaptureRegion]);

  const captureCanvas = useCallback(() => {
    CanvasCore.get().captureCanvas();
  }, []);

  const captureView = useCallback(() => {
    CanvasCore.get().captureView();
  }, []);

  const captureRegion = useCallback(() => {
    document.body.classList.add("is-capturing-region");
    updateRegionAnchor(null);
    updateRegionSelection(null);
    setCaptureRegionMode(true);
  }, [updateRegionAnchor, updateRegionSelection]);

  const getRegionPoint = useCallback(
    ({
      clientX,
      clientY,
    }: {
      clientX: number;
      clientY: number;
    }): RegionPoint | undefined => {
      try {
        const [canvasX, canvasY] = CanvasCore.get().screenToPos(
          clientX,
          clientY,
        );

        return {
          clientX,
          clientY,
          canvasX,
          canvasY,
        };
      } catch (error) {
        console.warn("Unable to read capture region point", error);
        return undefined;
      }
    },
    [],
  );

  const getRegionCanvasArea = useCallback(
    (start: RegionPoint, end: RegionPoint): CanvasCaptureArea => {
      const left = Math.min(start.canvasX, end.canvasX);
      const top = Math.min(start.canvasY, end.canvasY);
      const right = Math.max(start.canvasX, end.canvasX) + 1;
      const bottom = Math.max(start.canvasY, end.canvasY) + 1;

      return {
        x: left,
        y: top,
        width: right - left,
        height: bottom - top,
      };
    },
    [],
  );

  const clampRegionCanvasArea = useCallback(
    (area: CanvasCaptureArea): CanvasCaptureArea | undefined => {
      const [canvasWidth, canvasHeight] = canvasSize ?? [];
      if (!canvasWidth || !canvasHeight) return area;

      const left = Math.max(0, area.x);
      const top = Math.max(0, area.y);
      const right = Math.min(canvasWidth, area.x + area.width);
      const bottom = Math.min(canvasHeight, area.y + area.height);

      if (right <= left || bottom <= top) return undefined;

      return {
        x: left,
        y: top,
        width: right - left,
        height: bottom - top,
      };
    },
    [canvasSize],
  );

  const finishCaptureRegion = useCallback(
    (start: RegionPoint, end: RegionPoint) => {
      const area = getRegionCanvasArea(start, end);

      resetCaptureRegion();
      CanvasCore.get().captureRegion(area);
    },
    [getRegionCanvasArea, resetCaptureRegion],
  );

  useEffect(() => {
    if (!captureRegionMode || !isMobile) return;

    const panZoom = CanvasCore.get(false)?.getPanZoom();
    if (!panZoom) return;

    let projectionFrame: number | undefined;
    const refreshRegionProjection = () => {
      if (projectionFrame !== undefined || !regionSelectionRef.current) return;

      projectionFrame = window.requestAnimationFrame(() => {
        projectionFrame = undefined;
        const selection = regionSelectionRef.current;
        if (selection) updateRegionSelection({ ...selection });
      });
    };
    const handleMobileRegionPoint = ({
      clientX,
      clientY,
    }: {
      clientX: number;
      clientY: number;
    }) => {
      const point = getRegionPoint({ clientX, clientY });
      if (!point) {
        toast.info("Unable to capture region: can't find canvas");
        resetCaptureRegion();
        return;
      }

      const anchor = regionAnchorRef.current;
      if (anchor) {
        finishCaptureRegion(anchor, point);
        return;
      }

      updateRegionAnchor(point);
      updateRegionSelection({ start: point, current: point });
    };

    panZoom.on("click", handleMobileRegionPoint);
    panZoom.on("viewportMove", refreshRegionProjection);
    document.addEventListener("pointermove", refreshRegionProjection, true);

    return () => {
      if (projectionFrame !== undefined) {
        window.cancelAnimationFrame(projectionFrame);
      }
      panZoom.off("click", handleMobileRegionPoint);
      panZoom.off("viewportMove", refreshRegionProjection);
      document.removeEventListener(
        "pointermove",
        refreshRegionProjection,
        true,
      );
    };
  }, [
    captureRegionMode,
    finishCaptureRegion,
    getRegionPoint,
    isMobile,
    resetCaptureRegion,
    updateRegionAnchor,
    updateRegionSelection,
  ]);

  const handleCaptureRegionPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;

      const point = getRegionPoint(event);
      if (!point) {
        toast.info("Unable to capture region: can't find canvas");
        resetCaptureRegion();
        return;
      }

      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      updateRegionSelection({
        start: regionAnchorRef.current ?? point,
        current: point,
      });
    },
    [getRegionPoint, resetCaptureRegion, updateRegionSelection],
  );

  const handleCaptureRegionPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const point = getRegionPoint(event);
      if (!point) return;

      const selection = regionSelectionRef.current;
      if (selection) {
        updateRegionSelection({ ...selection, current: point });
        return;
      }

      if (regionAnchorRef.current) {
        updateRegionSelection({
          start: regionAnchorRef.current,
          current: point,
        });
      }
    },
    [getRegionPoint, updateRegionSelection],
  );

  const handleCaptureRegionPointerUp = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const point = getRegionPoint(event);
      const selection = regionSelectionRef.current;
      if (!point || !selection) return;

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      const dragDistance = Math.hypot(
        point.clientX - selection.start.clientX,
        point.clientY - selection.start.clientY,
      );

      if (!regionAnchorRef.current && dragDistance < REGION_CLICK_THRESHOLD) {
        updateRegionAnchor(point);
        updateRegionSelection({ start: point, current: point });
        return;
      }

      finishCaptureRegion(regionAnchorRef.current ?? selection.start, point);
    },
    [
      finishCaptureRegion,
      getRegionPoint,
      updateRegionAnchor,
      updateRegionSelection,
    ],
  );

  const handleCaptureRegionPointerCancel = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      updateRegionSelection(
        regionAnchorRef.current
          ? { start: regionAnchorRef.current, current: regionAnchorRef.current }
          : null,
      );
    },
    [updateRegionSelection],
  );

  const getTemplateCaptureArea = useCallback(() => {
    const canvasWidth = canvasSize?.[0];
    const canvasHeight = canvasSize?.[1];
    const dimensions = TemplateRenderer.instance?.getDimentions();
    let width = Math.round(dimensions?.display.width ?? 0);
    let height = Math.round(dimensions?.display.height ?? 0);

    if (!Number.isFinite(width) || width <= 0) {
      width = Math.round(template.width ?? 0);
    }

    if (!Number.isFinite(height) || height <= 0) {
      const templateCanvas =
        typeof document === "undefined"
          ? null
          : document.querySelector<HTMLCanvasElement>("#template canvas");

      if (templateCanvas && width > 0 && templateCanvas.width > 0) {
        height = Math.round(
          width * (templateCanvas.height / templateCanvas.width),
        );
      }
    }

    if (
      !template.url ||
      template.x === undefined ||
      template.y === undefined ||
      canvasWidth === undefined ||
      canvasHeight === undefined ||
      width <= 0 ||
      height <= 0
    ) {
      return undefined;
    }

    const left = Math.max(0, Math.floor(template.x) - TEMPLATE_CAPTURE_PADDING);
    const top = Math.max(0, Math.floor(template.y) - TEMPLATE_CAPTURE_PADDING);
    const right = Math.min(
      canvasWidth,
      Math.ceil(template.x + width) + TEMPLATE_CAPTURE_PADDING,
    );
    const bottom = Math.min(
      canvasHeight,
      Math.ceil(template.y + height) + TEMPLATE_CAPTURE_PADDING,
    );
    const captureWidth = right - left;
    const captureHeight = bottom - top;

    if (captureWidth <= 0 || captureHeight <= 0) {
      return undefined;
    }

    return {
      x: left,
      y: top,
      width: captureWidth,
      height: captureHeight,
    };
  }, [canvasSize, template.url, template.x, template.y, template.width]);

  const captureTemplateArea = useCallback(() => {
    const area = getTemplateCaptureArea();

    if (!area) {
      toast.info("Unable to capture template area: template is not ready");
      return;
    }

    CanvasCore.get().captureTemplateArea(area);
  }, [getTemplateCaptureArea]);

  const hasTemplateCapture = Boolean(getTemplateCaptureArea());
  const regionSelectionArea = regionSelection
    ? clampRegionCanvasArea(
        getRegionCanvasArea(regionSelection.start, regionSelection.current),
      )
    : undefined;

  const regionSelectionRect = regionSelectionArea
    ? (() => {
        try {
          return CanvasCore.get().canvasAreaToScreenRect(regionSelectionArea);
        } catch (error) {
          console.warn("Unable to project capture region", error);
          return undefined;
        }
      })()
    : undefined;

  const regionStatusText = regionAnchorRef.current
    ? "Pick opposite corner"
    : regionSelection
      ? "Selecting region"
      : "Pick first corner";

  const captureRegionOverlay =
    typeof document === "undefined"
      ? null
      : createPortal(
          <AnimatePresence>
            {captureRegionMode && (
              <motion.div
                key="capture-region"
                animate={{ opacity: 1 }}
                aria-label="Capture region mode"
                className={[
                  isMobile ? "pointer-events-none" : "pointer-events-auto",
                  "fixed inset-0 z-[10000] cursor-crosshair overflow-hidden",
                ].join(" ")}
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                onContextMenu={(event) => event.preventDefault()}
                onPointerCancel={handleCaptureRegionPointerCancel}
                onPointerDown={handleCaptureRegionPointerDown}
                onPointerMove={handleCaptureRegionPointerMove}
                onPointerUp={handleCaptureRegionPointerUp}
                role="application"
                style={{ background: "rgba(14, 165, 233, 0.06)" }}
                transition={{ duration: 0.12 }}
              >
                <div className="canvas-overlay-card pointer-events-none absolute left-4 top-4 z-10 px-3 py-2">
                  <div className="flex items-center gap-2 text-sm font-semibold leading-none">
                    <Scan aria-hidden="true" size={16} />
                    <span>Capture Region</span>
                  </div>
                  <div className="mt-1 text-xs text-black/65 dark:text-white/65">
                    {regionStatusText}
                  </div>
                  {regionSelectionArea && (
                    <div className="mt-1 font-mono text-xs text-black dark:text-white">
                      {regionSelectionArea.width} x{" "}
                      {regionSelectionArea.height}px
                    </div>
                  )}
                </div>
                {regionSelectionRect && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute z-10"
                    style={{
                      background: "rgba(56, 189, 248, 0.14)",
                      outline: "2px solid rgba(186, 230, 253, 0.98)",
                      height: regionSelectionRect.height,
                      left: regionSelectionRect.left,
                      top: regionSelectionRect.top,
                      width: regionSelectionRect.width,
                    }}
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        );

  const value = useMemo(
    () => ({
      captureCanvas,
      captureView,
      captureRegion,
      captureTemplateArea,
      hasTemplateCapture,
    }),
    [
      captureCanvas,
      captureView,
      captureRegion,
      captureTemplateArea,
      hasTemplateCapture,
    ],
  );

  return (
    <context.Provider value={value}>
      {children}
      <CaptureSnapshotFeedback />
      {captureRegionOverlay}
    </context.Provider>
  );
};